import { fork, takeLatest, call, put, all, select } from "redux-saga/effects";
import analyticsApi from "./analyticsApi";

import {
  fetchBasicAnalyticsRequest,
  fetchBasicAnalyticsSuccess,
  fetchBasicAnalyticsFailure,
  fetchPracticeSolutionRequest,
  fetchPracticeSolutionSuccess,
  fetchPracticeSolutionFailure,
  fetchTopicWiseComparisonRequest,
  fetchTopicWiseComparisonSuccess,
  fetchTopicWiseComparisonFailure,
  fetchSubjectWiseAnalysisRequest,
  fetchSubjectWiseAnalysisSuccess,
  fetchSubjectWiseAnalysisFailure,
  fetchOverallAnalyticsRequest,
  fetchOverallAnalyticsSuccess,
  fetchOverallAnalyticsFailure,
  fetchQuestionAnalyticsRequest,
  fetchQuestionAnalyticsSuccess,
  fetchQuestionAnalyticsFailure,
} from "./analyticsSlice";

function* fetchBasicAnalyticsWorker(action) {
  try {
    const { testType, testDetails } = yield select((state) => state.examSlice);
    const paperType = testType?.id || action.payload?.paperType;
    const paperId = testDetails?.paper_id || action.payload?.paperId;

    if (!paperId || !paperType) throw new Error("Missing paperId or paperType");

    const response = yield call(analyticsApi.getBasicAnalytics, {
      paperId,
      paperType,
    });

    yield put(fetchBasicAnalyticsSuccess(response));
  } catch (error) {
    yield put(fetchBasicAnalyticsFailure(error.message));
  }
}

function* fetchPracticeSolutionWorker(action) {
  try {
    const { testType, testDetails } = yield select((state) => state.examSlice);
    const paperType = testType?.id || action.payload?.paperType;
    const paperId = testDetails?.paper_id || action.payload?.paperId;
    const testId = testDetails?.test_id || action.payload?.testId;

    if (!paperId || !paperType || !testId)
      throw new Error("Missing params for practice");

    const response = yield call(analyticsApi.getPracticeSolution, {
      paperId,
      testId,
      paperType,
    });

    yield put(fetchPracticeSolutionSuccess(response));
  } catch (error) {
    yield put(fetchPracticeSolutionFailure(error.message));
  }
}

function* fetchTopicWiseComparisonWorker(action) {
  try {
    const { testType, testDetails } = yield select((state) => state.examSlice);
    const paperType = testType?.id || action.payload?.paperType;
    const paperId = testDetails?.paper_id || action.payload?.paperId;

    if (!paperId || !paperType)
      throw new Error("Missing paperId or paperType (topic)");

    const response = yield call(analyticsApi.getTopicWiseComparison, {
      paperId,
      paperType,
    });

    yield put(fetchTopicWiseComparisonSuccess(response));
  } catch (error) {
    yield put(fetchTopicWiseComparisonFailure(error.message));
  }
}

function* fetchSubjectWiseAnalysisWorker(action) {
  try {
    const { testType, testDetails } = yield select((state) => state.examSlice);
    const paperType = testType?.id || action.payload?.paperType;
    const paperId = testDetails?.paper_id || action.payload?.paperId;

    if (!paperId || !paperType) {
      throw new Error(
        `Invalid params: paperId=${paperId}, paperType=${paperType}`
      );
    }

    const response = yield call(analyticsApi.getSubjectWiseComparison, {
      paperId,
      paperType,
    });

    if (response?.status === 200 && response?.data?.subjects) {
      yield put(fetchSubjectWiseAnalysisSuccess(response));
    } else {
      throw new Error(response?.message || "Invalid subject data format");
    }
  } catch (error) {
    console.error("SubjectWiseAnalysis saga error:", error);
    yield put(fetchSubjectWiseAnalysisFailure(error.message));
  }
}

function* fetchOverallAnalyticsWorker(action) {
  try {
    const { testType, testDetails } = yield select((state) => state.examSlice);
    const paperType = testType?.id || action.payload?.paperType;
    const paperId = testDetails?.paper_id || action.payload?.paperId;
    const filter_by = action.payload?.filter_by || "tpq"; // default

    if (!paperId || !paperType)
      throw new Error("Missing paperId or paperType (overall)");

    const response = yield call(analyticsApi.getOverallAnalytics, {
      paperId,
      paperType,
      filter_by,
    });

    if (response?.status === 200 && response?.data) {
      yield put(fetchOverallAnalyticsSuccess(response));
;
    } else {
      throw new Error(response?.message || "Invalid overall analytics format");
    }
  } catch (error) {
    console.error("OverallAnalytics saga error:", error);
    yield put(fetchOverallAnalyticsFailure(error.message));
  }
}

function* fetchQuestionAnalyticsWorker(action) {
  try {
    const { testType, testDetails } = yield select((state) => state.examSlice);
    const paperType = testType?.id || action.payload?.paperType;
    const paperId = testDetails?.paper_id || action.payload?.paperId;
    const qid = action.payload?.qid;

    if (!paperId || !paperType || !qid)
      throw new Error("Missing params for question analytics");

    const response = yield call(analyticsApi.getQuestionAnalytics, {
      paperId,
      paperType,
      qid,
    });

    if (response?.status === 200 && response?.data) {
      yield put(fetchQuestionAnalyticsSuccess(response.data));
    } else {
      throw new Error(response?.message || "Invalid question analytics data");
    }
  } catch (error) {
    console.error("QuestionAnalytics saga error:", error);
    yield put(fetchQuestionAnalyticsFailure(error.message));
  }
}

function* watchAnalyticsSaga() {
  yield takeLatest(fetchBasicAnalyticsRequest.type, fetchBasicAnalyticsWorker);
  yield takeLatest(
    fetchPracticeSolutionRequest.type,
    fetchPracticeSolutionWorker
  );
  yield takeLatest(
    fetchTopicWiseComparisonRequest.type,
    fetchTopicWiseComparisonWorker
  );
  yield takeLatest(
    fetchSubjectWiseAnalysisRequest.type,
    fetchSubjectWiseAnalysisWorker
  );
  yield takeLatest(
    fetchOverallAnalyticsRequest.type,
    fetchOverallAnalyticsWorker
  );
  yield takeLatest(
    fetchQuestionAnalyticsRequest.type,
    fetchQuestionAnalyticsWorker
  );
}

export default function* analyticsSaga() {
  yield all([fork(watchAnalyticsSaga)]);
}
